import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "side-nav-container",
  "data-test": "side-nav"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "burger-menu" }, null, -1)
const _hoisted_3 = [
  _hoisted_2
]

import { ref, watch, onMounted, onBeforeMount } from "vue";
import { isMobile } from "mobile-device-detect";
import d from "debug";
import { csContext } from "../../../services/cs-context";
import router from "../../../router/router";
import SideNavMenu from "./side-nav-menu.vue";
import {
  buildSideNavSections,
  getTeamNavItems,
  getWatchGroupNavItems,
  getResourcesNavItems,
  getOrganisationNavItems,
  getUserState,
  checkAccreditedFunctionality,
} from "./side-nav.utils";
import { SideNavSections, SideNavOptions, Menu } from "./side-nav.types";


export default /*#__PURE__*/_defineComponent({
  __name: 'side-nav',
  props: {
    sideNavIsOpen: { type: Boolean }
  },
  emits: ["nav-state-changed"],
  setup(__props: any, { emit: __emit }) {

const debug = d("cs.Router");

const props = __props;

const emit = __emit;

const navSections = ref<SideNavSections>();
const allMenus = ref<Menu[]>([]);
const currentOpenMenu = ref<Menu | null>(null);
const windowHeight = ref(window.innerHeight);
const navOptions = ref<SideNavOptions | null>(null);
const routeSwitch = ref(false);
const isTabletOrMobile = ref(isMobile);

function openNav(): void {
  emit("nav-state-changed", true);
}

function closeNav(): void {
  emit("nav-state-changed", false);
}

function openMenu({
  menuToOpenName,
  hideMenu,
}: {
  menuToOpenName: string;
  hideMenu: string;
}): void {
  const menuToOpen = allMenus.value.find((menu) => menu.name === menuToOpenName);
  const menuToHide = allMenus.value.find((menu) => menu.name === hideMenu);
  if (!menuToOpen || !menuToHide) return;
  menuToOpen.isOpen = true;
  menuToHide.isHiddenLeft = true;
  currentOpenMenu.value = menuToOpen;
  currentOpenMenu.value.parentMenu = menuToHide;
}

function closeMenu(): void {
  if (!currentOpenMenu.value) return;

  currentOpenMenu.value.isOpen = false;

  if (currentOpenMenu.value.parentMenu) {
    currentOpenMenu.value = currentOpenMenu.value.parentMenu;
    currentOpenMenu.value.isHiddenLeft = false;
  }
}

function setUserState() {
  navOptions.value = getUserState();
}

function setSideNavSections() {
  if (navOptions.value) {
    navSections.value = buildSideNavSections(navOptions.value);
  }
}

function setAllMenus() {
  if (!navSections.value) return;
  
  const organisationSection = navSections.value.items.find(({ name }) => name === "My Organisations");
  const organisationItems = organisationSection?.items.filter(({ id }) => !!id) || [];
  
  allMenus.value = [
    navSections.value,
    ...navSections.value.items,
    ...navSections.value.items
      .find(({ name }) => name === "My Teams")!
      .items.filter(({ id }) => !!id),
    ...organisationItems,
    ...navSections.value.items.find(({ name }) => name === "Resources")!.items,
  ].map((menu) => ({
    ...menu,
    isHiddenLeft: false,
    isOpen: menu.id === "home",
  }));
}

async function integrateTeamData() {
  const teams = await getTeamNavItems();
  if (!navSections.value) return;

  navSections.value.items[0].items = [...teams, ...navSections.value.items[0].items];
}

async function integratePrismicData() {
  if (!navSections.value) return;

  navSections.value.items.find((i) => i.name === "Resources")!.items = await getResourcesNavItems();
}

async function integrateWatchGroupsData() {
  const watchGroups = csContext.userDocument.groupsWatched;

  if (!navSections.value) return;

  if (watchGroups.length > 0) {
    const watchGroupItems = getWatchGroupNavItems(watchGroups);
    navSections.value.items.find((i) => i.name === "Watch Groups")!.items = watchGroupItems;
  }
}

async function integrateOrganisationData() {
  if (!navSections.value) return;

  const organisationSection = navSections.value.items.find((i) => i.name === "My Organisations");
  if (organisationSection) {
    const organisations = await getOrganisationNavItems();
    organisationSection.items = organisations;
  }
}

async function integrateAccreditedFunctionality() {
  if (!navOptions.value) return;
  
  const { hasAccess, organisations } = await checkAccreditedFunctionality();
  navOptions.value.hasAccreditedFunctionality = hasAccess;
  
  // If accredited functionality is available, add the organisation section and populate it
  if (hasAccess && navSections.value) {
    // Check if organisation section already exists
    const organisationExists = navSections.value.items.find(item => item.name === "My Organisations");
    
    if (!organisationExists) {
      // Find the index of Resources section to insert before it
      const resourcesIndex = navSections.value.items.findIndex(item => item.name === "Resources");
      navSections.value.items.splice(resourcesIndex, 0, {
        name: "My Organisations",
        navClosedImage: require("../../../assets/side-nav/Organisation-icon-reversed.svg"),
        navOpenImage: require("../../../assets/side-nav/Organisation-icon-forward.svg"),
        activePaths: ["/myorganisation"],
        activePriority: 4,
        divider: false,
        items: [],
      });
    }
    
    await integrateOrganisationData();
  }
}

async function handleAsyncActions() {
  await integrateTeamData();
  await integrateWatchGroupsData();
  await integratePrismicData();
  await integrateAccreditedFunctionality();
}

function setWindowHeight() {
  windowHeight.value = window.innerHeight;
}

onBeforeMount(() => {
  setUserState();
  setSideNavSections();
  handleAsyncActions().then((n) => setAllMenus());
  setAllMenus();

  // Methods to adjust window size to enable proper scrolling on mobile.
  window.addEventListener("resize", setWindowHeight);
});

watch(
  () => props.sideNavIsOpen,
  () => {
    document.body.style.overflow = props.sideNavIsOpen ? "hidden" : "initial";
  },
);

watch(
  () => props.sideNavIsOpen,
  () => {
    if (!props.sideNavIsOpen) {
      setAllMenus();
    }
  },
);

onMounted(() => {
  router.beforeResolve((to, from, next) => {
    routeSwitch.value = !routeSwitch.value;
    debug(`Routing to ${to.fullPath} from ${from.fullPath}`, { to, from });
    next();
  });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass(["side-nav-blackout", { 'show-blackout': _ctx.sideNavIsOpen }])
    }, null, 2),
    _createElementVNode("nav", {
      onMouseleave: closeNav,
      onBlur: closeNav,
      class: _normalizeClass([{ 'nav-open': _ctx.sideNavIsOpen }, "side-nav"]),
      id: "side-nav"
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(allMenus.value, (menu) => {
        return (_openBlock(), _createBlock(SideNavMenu, {
          key: `${menu.name} ${routeSwitch.value}`,
          "menu-data": menu,
          "side-nav-is-open": _ctx.sideNavIsOpen,
          "window-height": windowHeight.value,
          onOpenNav: openNav,
          onOpenMenu: openMenu,
          onCloseMenu: closeMenu
        }, null, 8, ["menu-data", "side-nav-is-open", "window-height"]))
      }), 128))
    ], 34),
    _createElementVNode("button", {
      class: _normalizeClass(["container side-nav-toggle-btn", { show: isTabletOrMobile.value && _ctx.sideNavIsOpen }]),
      onClick: closeNav,
      type: "button"
    }, _hoisted_3, 2)
  ]))
}
}

})